import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnnouncementType } from '@/shared/api/announcements';
import { useActiveAnnouncements, useDismissAnnouncement, useMarkAnnouncementSeen } from './useAnnouncements';
import styles from './AnnouncementBanner.module.css';

const TYPE_ICON: Record<AnnouncementType, string> = {
  info: 'fa-circle-info',
  success: 'fa-circle-check',
  warning: 'fa-triangle-exclamation',
  critical: 'fa-circle-exclamation',
};

/**
 * Renders active banner-style announcements at the top of the app shell. Critical
 * announcements are shown as a modal instead (see AnnouncementModal); an announcement
 * set to `both` appears in both places.
 */
export function AnnouncementBanner() {
  const { t } = useTranslation();
  const { data } = useActiveAnnouncements();
  const dismiss = useDismissAnnouncement();
  const markSeen = useMarkAnnouncementSeen();

  const banners = (data ?? []).filter((a) => a.display === 'banner' || a.display === 'both');
  const seenKey = banners.map((a) => a._id).join(',');

  useEffect(() => {
    banners.forEach((a) => markSeen.mutate(a._id));
    // Re-run only when the set of shown banners changes; the mutation object is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seenKey]);

  if (banners.length === 0) return null;

  return (
    <div className={styles.stack}>
      {banners.map((a) => (
        <div key={a._id} className={`${styles.banner} ${styles[a.type]}`} role="status">
          <i className={`fa-solid ${TYPE_ICON[a.type]} ${styles.icon}`} aria-hidden="true" />
          <div className={styles.content}>
            <p className={styles.title}>{a.title}</p>
            <p className={styles.body}>{a.body}</p>
          </div>
          {a.dismissible && (
            <button
              type="button"
              className={styles.dismiss}
              onClick={() => dismiss.mutate(a._id)}
              aria-label={t('announcement.dismiss')}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
